import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Typography,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_TableInstance,
} from "material-react-table";
import { Container, Row, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GetAppIcon from "@mui/icons-material/GetApp";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import Popover from "@mui/material/Popover";
import {
  ACCEPTED_EXTENSIONS,
  ActiveTab,
  ActiveTabEnum,
  BreakdownReasonDetail,
  BreakReasonItem,
  BuySell,
  GroupBreakdownResponse,
  GroupBreakdownTrade,
  ReconCombinedGroup,
  ReconFilterFormData,
  ReconResultsBySide,
  ReconRunResponse,
  ReconStatus,
  ReconSummary,
  ReconTradeItem,
  SelectOption,
} from "../../../Types/Reconautomation.types";
import {
  fetchBrokerOptions,
  fetchContractOptions,
  fetchGroupBreakdown,
  runReconciliation,
} from "./reconautomation.api";
import {
  CompactSelect,
  CompactTextField,
} from "../../../Components/SharedComponents/SharedComponents";
import { BrokerOption } from "../../ApiSettings/ApiBrokerSettings";
import { DateFormatErrorModal, ValidationError } from "./dateFormateErrorModal";
import { exportReconCSV } from "./Reconexport.utils";
import { useNavigate } from "react-router-dom";

// ── Constants ────────────────────────────────────────────────────────────────

const BUY_SELL_OPTIONS: SelectOption[] = [
  { label: "All", value: "" },
  { label: "Buy", value: "buy" },
  { label: "Sell", value: "sell" },
];
const CONTRACT_ALL_OPTION: SelectOption = { label: "\u200B All", value: "" };
const DEFAULT_SOURCE = "Britannia" as const;

const INITIAL_FILTERS: ReconFilterFormData = {
  source: DEFAULT_SOURCE,
  start_date: "",
  end_date: "",
  contract: "",
  buy_sell: "",
};

const EMPTY_RESULTS: ReconResultsBySide = { acers: [], bgm: [] };

const STATUS_COLOR: Record<string, "success" | "error" | "warning" | "default"> = {
  MATCHED: "success",
  MISMATCH_PRICE: "error",
  MISMATCH_QUANTITY: "error",
  MISMATCH_MARKUP: "warning",
  MISMATCH_PRICE_AND_MARKUP: "error",
  UNMATCHED_BGM: "warning",
  UNMATCHED_ACERS: "warning",
  CANON_FAILED: "default",
  MISMATCH_PRICE_AND_QUANTITY: "error",
  SUCCESS: "success",
  INVALID_QUANTITY: "warning",
  FAILURE: "error",
  SOURCE_ISSUE: "error",
};

const STATUS_LABEL: Record<string, string> = {
  MATCHED: "Matched",
  MISMATCH_PRICE: "Price Mismatch",
  MISMATCH_QUANTITY: "Quantity Mismatch",
  MISMATCH_MARKUP: "Markup Mismatch",
  MISMATCH_PRICE_AND_MARKUP: "Price & Markup",
  UNMATCHED_BGM: "Unmatched BGM",
  UNMATCHED_ACERS: "Unmatched ACERS",
  MISMATCH_PRICE_AND_QUANTITY: "Price & Quantity MisMatch",
  CANON_FAILED: "Canon Failed",
  SOURCE_ISSUE: "Source Issue",
  INVALID_QUANTITY: "Invalid Quantity",
  FAILURE: "Failure",
};

const SUMMARY_CARDS: { key: keyof ReconSummary; label: string; accent: string }[] = [
  { key: "total_bgm_trades", label: "BGM Trades", accent: "#1565c0" },
  { key: "total_acers_trades", label: "ACERS Trades", accent: "#1565c0" },
  { key: "matched", label: "Matched", accent: "#2e7d32" },
  { key: "mismatch_price", label: "Price Mismatch", accent: "#c62828" },
  { key: "mismatch_quantity", label: "Quantity Mismatch", accent: "#e65100" },
  { key: "mismatch_price_and_quantity", label: "Price & Quantity Mismatch", accent: "#c62828" },
  { key: "unmatched_bgm", label: "Unmatched BGM", accent: "#f57c00" },
  { key: "unmatched_acers", label: "Unmatched ACERS", accent: "#f57c00" },
  { key: "canon_failed", label: "Mapping Failed", accent: "#555" },
];

const INPUT_SX = {
  "& .MuiInputBase-root": { fontSize: "0.7rem", height: "32px" },
  "& .MuiInputLabel-root": {
    fontSize: "0.7rem",
    transform: "translate(14px, 8px) scale(1)",
    "&.MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#C0C0C0" },
  minWidth: 130,
  flex: "1 1 130px",
  maxWidth: 200,
} as const;

const ROW_HEIGHT = 30;

const COMMON_TABLE_PROPS = {
  enableDensityToggle: false,
  enableColumnOrdering: true,
  enableColumnFilterModes: false,
  enableStickyHeader: true,
  enableColumnPinning: true,
  enableGrouping: true,
  initialState: { showColumnFilters: false, density: "compact" as const, columnPinning: { left: ["expand"] as string[] }, columnVisibility: { canonicalisation_status: false } },
  muiTableHeadCellProps: {
    sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal", fontSize: "0.72rem" },
  },
  muiTableBodyRowProps: {
    sx: { height: ROW_HEIGHT, maxHeight: ROW_HEIGHT },
  },
  muiTableBodyCellProps: {
    sx: {
      border: "1px solid #ebe9e98f",
      fontSize: "0.73rem",
      height: ROW_HEIGHT,
      maxHeight: ROW_HEIGHT,
      py: 0,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  },
  muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0" } },
  muiTableContainerProps: { sx: { backgroundColor: "#fff" }, className: "table-container-wrapper__new" },
  muiTopToolbarProps: { className: "top-toolbar-wrapper__new", sx: { backgroundColor: "#fff", padding: "8px" } },
  muiBottomToolbarProps: { className: "bottom-toolbar-wrapper__new", sx: { backgroundColor: "#fff", padding: "8px" } },
  muiTableFooterProps: { sx: { backgroundColor: "#fff" }, className: "table-footer-wrapper__new" },
} as const;

const BREAKDOWN_TABLE_PROPS = {
  ...COMMON_TABLE_PROPS,
  muiTableHeadCellProps: {
    sx: { border: "1px solid #e0e0e087", fontSize: "0.65rem", padding: "2px 6px" },
  },
  muiTableBodyCellProps: {
    sx: { border: "1px solid #ebe9e98f", fontSize: "0.68rem", padding: "2px 6px" },
  },
} as const;

const MISMATCH_STATUSES = [
  "MISMATCH_PRICE",
  "MISMATCH_MARKUP",
  "MISMATCH_PRICE_AND_MARKUP",
  "MISMATCH_QUANTITY",
  "MISMATCH_PRICE_AND_QUANTITY",
];

const SIDE_THEME = {
  acers: { accent: "#1565c0", border: "#90caf9", bg: "linear-gradient(180deg,#eef7fb 0%,#e3f2fd 100%)", chipBg: "#bbdefb" },
  bgm: { accent: "#2e7d32", border: "#a5d6a7", bg: "linear-gradient(180deg,#eff8ef 0%,#e8f5e9 100%)", chipBg: "#c8e6c9" },
} as const;

const VIEW_TYPE_STORAGE_KEY = "recon_view_type";
type ViewType = "version-1" | "version-2";

// ── Small presentational helpers ────────────────────────────────────────────

const SummaryCard: React.FC<{ label: string; value: number; accent?: string }> = ({
  label,
  value,
  accent = "#00bcd4",
}) => (
  <Box
    sx={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      p: "10px 14px", borderRadius: "6px", border: `1px solid ${accent}40`,
      backgroundColor: `${accent}0d`, minWidth: 80, flex: "1 1 80px",
    }}
  >
    <Typography sx={{ fontSize: "1.35rem", fontWeight: 700, lineHeight: 1, color: accent }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.6rem", color: "#777", mt: 0.5, textAlign: "center", lineHeight: 1.2 }}>
      {label}
    </Typography>
  </Box>
);

const StatusChip: React.FC<{ status: string | null }> = ({ status }) =>
  !status ? (
    <Typography sx={{ fontSize: "0.7rem", color: "#ccc" }}>—</Typography>
  ) : (
    <Chip label={STATUS_LABEL[status] ?? status} color={STATUS_COLOR[status] ?? "default"} size="small" sx={{ fontSize: "0.62rem", height: 16 }} />
  );

const directionCell = (val: string) => {
  if (!val) {
    return <Typography sx={{ fontSize: "0.72rem", color: "#ccc" }}>—</Typography>;
  }

  const normalized = val.trim().toUpperCase();
  const isBuy = normalized === "B" || normalized === "BUY";

  return (
    <Typography
      sx={{
        fontSize: "0.72rem",
        fontWeight: 700,
        color: isBuy ? "#1565c0" : "#c62828",
      }}
    >
      {val}
    </Typography>
  );
};
const qtyPriceCell = (val: number | null) => (
  <Typography sx={{ fontSize: "0.73rem", color: val == null ? "#ccc" : "inherit" }}>
    {val ?? "—"}
  </Typography>
);

/** Generic "first item + N more" popover chip. Backs the trade-id cell and both
 *  break-reason cells, which previously each carried their own copy of this UI. */
function OverflowChip<T>({
  items,
  renderPrimary,
  renderDetail,
  emptyLabel = "—",
}: {
  items: T[];
  renderPrimary: (item: T) => React.ReactNode;
  renderDetail: (item: T, idx: number) => React.ReactNode;
  emptyLabel?: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (items.length === 0) {
    return <Typography sx={{ fontSize: "0.7rem", color: "#ccc" }}>{emptyLabel}</Typography>;
  }

  const extraCount = items.length - 1;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, py: 0.3, flexWrap: "nowrap", overflow: "hidden" }}>
      {renderPrimary(items[0])}

      {extraCount > 0 && (
        <>
          <Chip
            label={`+${extraCount} more`}
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              fontSize: "0.58rem", height: 16, cursor: "pointer",
              backgroundColor: "#fdecea", color: "#c62828", fontWeight: 700,
              "& .MuiChip-label": { px: 0.7 },
            }}
          />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box sx={{ minWidth: 220, maxWidth: 320 }}>
              <Typography sx={{ fontSize: "0.66rem", fontWeight: 800, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, px: 1.2, pt: 1, pb: 0.6 }}>
                All ({items.length})
              </Typography>
              {items.map((item, idx) => renderDetail(item, idx))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
}

/** Single row inside an OverflowChip popover — just a bolded label line.
 *  Replaces the previously-duplicated ReasonDetailRow / BreakdownReasonDetailRow. */
const PopoverDetailRow: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={{ py: 0.8, px: 1.2, borderBottom: "1px solid #f0f0f0", "&:last-of-type": { borderBottom: "none" } }}>
    <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#c62828" }}>{label}</Typography>
  </Box>
);

const TradeIdCell: React.FC<{ ids?: string[] | null }> = ({ ids: rawIds }) => {
  const ids = (rawIds ?? []).filter(Boolean);
  return (
    <OverflowChip
      items={ids}
      renderPrimary={(id) => (
        <Typography sx={{ fontSize: "0.7rem", fontFamily: "monospace", color: "#1565c0" }}>{id}</Typography>
      )}
      renderDetail={(id, i) => (
        <Typography key={i} sx={{ fontSize: "0.72rem", fontFamily: "monospace", color: "#1565c0", px: 1.2, py: 0.4 }}>
          {id}
        </Typography>
      )}
    />
  );
};

const BreakReasonCell: React.FC<{ reasons?: BreakReasonItem[] | null }> = ({ reasons }) => (
  <OverflowChip
    items={reasons ?? []}
    renderPrimary={(r) => (
      <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: "#c62828", lineHeight: 1.3 }}>
        {r.reason}
      </Typography>
    )}
    renderDetail={(r, i) => <PopoverDetailRow key={i} label={r.reason} />}
  />
);

const BreakdownReasonCell: React.FC<{ breakReasons?: BreakdownReasonDetail[] | null }> = ({ breakReasons }) => {
  // Flatten: each break_reason entry can itself carry multiple reason strings.
  const flatReasons = (breakReasons ?? []).flatMap((detail) => detail.reasons ?? []);

  return (
    <OverflowChip
      items={flatReasons}
      renderPrimary={(reasonText) => (
        <Typography sx={{ fontSize: "0.66rem", fontWeight: 700, color: "#c62828", lineHeight: 1.3 }}>
          {reasonText}
        </Typography>
      )}
      renderDetail={(reasonText, i) => <PopoverDetailRow key={i} label={reasonText} />}
    />
  );
};

const SummaryField: React.FC<{ label: string; value: React.ReactNode; accent?: string }> = ({
  label,
  value,
  accent = "#333",
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2, px: 1.4, py: 0.6, borderRight: "1px solid #dceef5", minWidth: 90 }}>
    <Typography sx={{ fontSize: "0.58rem", color: "#8a99a3", textTransform: "uppercase", letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: accent, lineHeight: 1.3 }}>
      {value}
    </Typography>
  </Box>
);

const DetailTotalsRow: React.FC<{ label: string; total: number | null; accent: string; bg: string }> = ({
  label,
  total,
  accent,
  bg,
}) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      px: 1.2, py: 0.5, backgroundColor: bg, borderTop: `1px solid ${accent}55`,
    }}
  >
    <Typography sx={{ fontSize: "0.64rem", fontWeight: 800, color: accent, textTransform: "uppercase", letterSpacing: 0.5 }}>
      Total
    </Typography>
    <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: accent }}>
      {label}: {total ?? "—"}
    </Typography>
  </Box>
);

/** Shared header + MaterialReactTable panel — used for both the main
 *  ACERS/BGM tables and the breakdown ACERS/BGM tables (4 call sites total). */
function SidePanel<T extends Record<string, any>>({
  side,
  label,
  count,
  table,
  footer,
}: {
  side: keyof typeof SIDE_THEME;
  label: string;
  count: number;
  table: MRT_TableInstance<T>;
  footer?: React.ReactNode;
}) {
  const theme = SIDE_THEME[side];
  return (
    <Box sx={{ border: `1px solid ${theme.border}`, borderRadius: "6px", overflow: "hidden", display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{
        px: 1.2, py: 0.6, background: theme.bg, borderBottom: `1px solid ${theme.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 800, color: theme.accent, textTransform: "uppercase", letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <Box sx={{ fontSize: "0.62rem", fontWeight: 700, color: theme.accent, backgroundColor: theme.chipBg, px: 1, py: 0.2, borderRadius: "10px" }}>
          {count} trades
        </Box>
      </Box>
      <div className="table-ad__wrapper" style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <div className="table-ad__wrapperCard">
          <MaterialReactTable table={table} />
        </div>
      </div>
      {footer}
    </Box>
  );
}

// ── Data helpers ─────────────────────────────────────────────────────────────

const buildCombinedGroups = (
  acers: ReconTradeItem[],
  bgm: ReconTradeItem[]
): ReconCombinedGroup[] => {
  const map = new Map<string, ReconCombinedGroup>();

  acers.forEach((item) => {
    map.set(item.group_id, { group_id: item.group_id, status: item.status, acers: item });
  });

  bgm.forEach((item) => {
    const existing = map.get(item.group_id);
    if (existing) {
      existing.bgm = item;
      // If one side is clean but the other flags an issue, surface the issue.
      if (existing.status === "MATCHED" && item.status !== "MATCHED") {
        existing.status = item.status;
      }
    } else {
      map.set(item.group_id, { group_id: item.group_id, status: item.status, bgm: item });
    }
  });

  return Array.from(map.values());
};

const hasCanonFailure = (item?: ReconTradeItem) => {
  const status = item?.canonicalisation_status;
  if (!status || status.length === 0) return false;
  return status.some((s) => s !== "SUCCESS");
};

const isSourceIssueGroup = (g: ReconCombinedGroup) =>
  g.bgm?.status === "SOURCE_ISSUE" || g.acers?.status === "SOURCE_ISSUE";

const isFailureGroup = (g: ReconCombinedGroup) =>
  g.bgm?.status === "FAILURE" || g.acers?.status === "FAILURE";

// ── Reusable hooks ───────────────────────────────────────────────────────────
/** Two scrollable panes that mirror each other's scroll position.
 *  Used for the main ACERS/BGM tables and again for the breakdown tables. */
function useSyncedScroll() {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);

  const sync = useCallback((source: "left" | "right") => {
    const src = source === "left" ? leftRef.current : rightRef.current;
    const dst = source === "left" ? rightRef.current : leftRef.current;
    if (!src || !dst || isSyncing.current) return;

    isSyncing.current = true;
    dst.scrollTop = src.scrollTop;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => {
      isSyncing.current = false;
    });
  }, []);

  return { leftRef, rightRef, sync };
}

type AlignedRow = ReconTradeItem & { is_placeholder?: boolean };

const placeholderRow = (g: ReconCombinedGroup): AlignedRow => ({
  group_id: g.group_id,
  status: "" as ReconStatus,   
  trade_date: null,
  client: null,
  contract: null,
  contract_month: null,
  direction: "",               
  quantity: null,
  price: null,
  reasons: [],
  canonicalisation_status: [],
  trade_ids: [],
  is_placeholder: true,
});

const buildAlignedSides = (
  groups: ReconCombinedGroup[]
): { acers: AlignedRow[]; bgm: AlignedRow[] } => {
  const acers: AlignedRow[] = [];
  const bgm: AlignedRow[] = [];

  groups.forEach((g) => {
    acers.push(g.acers ?? placeholderRow(g));
    bgm.push(g.bgm ?? placeholderRow(g));
  });

  return { acers, bgm };
};


/** Builds a MaterialReactTable instance with the shared recon table config,
 *  used for both the main side tables and the breakdown side tables. */
function useSideTable<T extends Record<string, any>>(opts: {
  columns: MRT_ColumnDef<T>[];
  data: T[];
  getRowId: (row: T, index: number) => string;
  scrollRef: React.RefObject<HTMLDivElement>;
  onScroll: () => void;
  breakdown?: boolean;
  rowSx?: (row: T) => object;
  csvExport?: () => void;
  firstColumnId?: string;
}) {
  const base = opts.breakdown ? BREAKDOWN_TABLE_PROPS : COMMON_TABLE_PROPS;

  const breakdownInitialState = opts.firstColumnId
    ? {
      ...base.initialState,
      columnPinning: { left: [], right: [] },
      columnOrder: [
        opts.firstColumnId,
        ...opts.columns
          .map((c) => (c as any).accessorKey ?? c.id)
          .filter((id) => id !== opts.firstColumnId),
      ],
    }
    : base.initialState;

  return useMaterialReactTable<T>({
    ...base,
    columns: opts.columns,
    data: opts.data,
    getRowId: opts.getRowId,
    enablePagination: !opts.breakdown,
    enableBottomToolbar: !opts.breakdown,
    initialState: opts.breakdown
      ? breakdownInitialState
      : { ...base.initialState, pagination: { pageSize: 30, pageIndex: 0 }, columnVisibility: { canonicalisation_status: false } },
    muiTableContainerProps: { ...base.muiTableContainerProps, ref: opts.scrollRef, onScroll: opts.onScroll },
    ...(opts.rowSx && {
      muiTableBodyRowProps: ({ row }: any) => ({ sx: opts.rowSx!(row.original) }),
    }),
    ...(opts.csvExport && {
      renderTopToolbarCustomActions: () => (
        <Button onClick={opts.csvExport} startIcon={<BrowserUpdatedIcon />}>
          CSV
        </Button>
      ),
    }),
  });
}

// ── Component ────────────────────────────────────────────────────────────────
const ReconciliationAutomationPage: React.FC = () => {
  const [sourceOptions, setSourceOptions] = useState<SelectOption[]>([]);
  const [contractOptions, setContractOptions] = useState<SelectOption[]>([]);
  const [filters, setFilters] = useState<ReconFilterFormData>(INITIAL_FILTERS);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [lastFileName, setLastFileName] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [summary, setSummary] = useState<ReconSummary | null>(null);
  const [results, setResults] = useState<ReconResultsBySide>(EMPTY_RESULTS);
  const [activeTab, setActiveTab] = useState<ActiveTab>("all");
  const [formatErrors, setFormatErrors] = useState<ValidationError[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Aggregation breakdown (expand-on-click for a Group ID) ──────────────
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<GroupBreakdownResponse | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const breakdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const [viewType, setViewType] = useState<ViewType>(() => {
    const stored = localStorage.getItem(VIEW_TYPE_STORAGE_KEY);
    return stored === "version-1" || stored === "version-2"
      ? stored
      : "version-2";
  });

  const mainScroll = useSyncedScroll();
  const detailScroll = useSyncedScroll();

  // ── Filter / run handlers ────────────────────────────────────────────────
  const updateFilter = useCallback((key: keyof ReconFilterFormData, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    if (breakdown && breakdownRef.current) {
      requestAnimationFrame(() => {
        breakdownRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    }
  }, [breakdown]);

  const handleViewTypeChange = useCallback(
  (event: React.ChangeEvent<HTMLInputElement>) => {
    const newViewType = event.target.value as ViewType;
    setViewType(newViewType);
    localStorage.setItem(VIEW_TYPE_STORAGE_KEY, newViewType);

    if (newViewType === "version-1") {
      navigate(`${process.env.PUBLIC_URL}/data-table/reconciliation-automation-version-1`);
    } else {
      navigate(`${process.env.PUBLIC_URL}/data-table/reconciliation-automation-version-2`);
    }
  },
  [navigate]
);

  const handleClear = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setSelectedFile(null);
    setSummary(null);
    setResults(EMPTY_RESULTS);
    setHasRun(false);
    setRunId(null);
    setExpandedGroupId(null);
    setLastFileName(null);
    setBreakdown(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file format "${ext}". Please upload .xlsx, .xlsm, or .csv only.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSelectedFile(file);
    setLastFileName(file.name);
  }, []);

  const applyResults = useCallback((data: ReconRunResponse) => {
    const { acers, bgm } = data.results;
    const combined = buildCombinedGroups(acers, bgm);

    const computedSummary: ReconSummary = {
      total_bgm_trades: bgm.length,
      total_acers_trades: acers.length,
      matched: combined.filter((g) => g.status === "MATCHED").length,
      mismatch_price: combined.filter((g) => g.status === "MISMATCH_PRICE").length,
      mismatch_quantity: combined.filter((g) => g.status === "MISMATCH_MARKUP").length,
      mismatch_price_and_quantity: combined.filter((g) => g.status === "MISMATCH_PRICE_AND_MARKUP").length,
      unmatched_bgm: bgm.filter((x) => x.status === "UNMATCHED_BGM").length,
      unmatched_acers: acers.filter((x) => x.status === "UNMATCHED_ACERS").length,
      canon_failed: combined.filter(isFailureGroup).length,
    };

    setSummary(computedSummary);
    setResults({ acers, bgm });
    setRunId(null); // set from response if/when the API returns a run_id
    setHasRun(true);
    setActiveTab("all");
    setExpandedGroupId(null);
    setBreakdown(null);
    setSelectedFile(null); 
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleExpandClick = useCallback(async (groupId: string) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      setBreakdown(null);
      return;
    }
    setExpandedGroupId(groupId);
    setBreakdown(null);
    setBreakdownLoading(true);
    try {
      const data = await fetchGroupBreakdown(groupId);
      setBreakdown(data);
    } catch (err) {
      toast.error("Failed to load aggregation breakdown.");
      console.error(err);
      setExpandedGroupId(null);
    } finally {
      setBreakdownLoading(false);
    }
  }, [expandedGroupId]);

  const handleRun = useCallback(async () => {
    if (!filters.start_date || !filters.end_date) return toast.error("Please select a date range.");
    if (!filters.source) return toast.error("Please select a source.");
    if (!selectedFile) return toast.error("Please upload a BGM broker file.");
    setIsRunning(true);
    try {
        try {
      await selectedFile.slice(0, 1).arrayBuffer();
    } catch {
      toast.error("This file has changed since you selected it. Please re-upload it and run again.");
      setSelectedFile(null);
      setHasRun(false); 
      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsRunning(false);
      return;
    }
      const data = await runReconciliation({
        source: filters.source,
        start_date: filters.start_date,
        end_date: filters.end_date,
        file: selectedFile,
        ...(filters.contract && { contract: filters.contract }),
        ...(filters.buy_sell && { buy_sell: filters.buy_sell as BuySell }),
      });
      applyResults(data);
      toast.success("Reconciliation completed successfully!");
    } catch (err: any) {
      const details = err?.response?.data?.details;
      if (details?.status === "partial" && details?.errors?.length) {
        setFormatErrors(details.errors);
      } else if (err?.name === "NotFoundError" || err?.name === "NotReadableError") {
        toast.error("The selected file could not be read — it may have changed. Please re-upload it.");
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      else {
        toast.error("Reconciliation failed. Please try again.");
        console.error(err);
      }
    } finally {
      setIsRunning(false);
    }
  }, [filters, selectedFile, applyResults]);

  useEffect(() => {
    fetchBrokerOptions().then(({ data, error }) => {
      if (error || !Array.isArray(data)) {
        toast.error(`Error loading sources: ${error ?? "Unknown error"}`);
        setSourceOptions([]);
      } else {
        setSourceOptions(data.map((item: BrokerOption) => ({ value: item.name, label: item.name })));
        setFilters((prev) => ({ ...prev, source: DEFAULT_SOURCE }));
      }
    });

    fetchContractOptions()
      .then((opts) => setContractOptions([CONTRACT_ALL_OPTION, ...opts]))
      .catch(() => toast.error("Error loading contract options"));
  }, []);

  const handleDownloadTemplate = () => {
    const a = document.createElement("a");
    a.href = "/templates/recon-template.csv";
    a.download = "recon-template.csv";
    a.click();
  };

  // ── Combined per-group view (drives tab counts + summary + failure detection) ──
  const combinedGroups = useMemo(
    () => buildCombinedGroups(results.acers, results.bgm),
    [results]
  );

  const groupIdsByTab = useMemo(() => {
    const matchedIds = new Set(combinedGroups.filter((g) => g.status === "MATCHED").map((g) => g.group_id));
    const mismatchIds = new Set(
      combinedGroups.filter((g) => MISMATCH_STATUSES.includes(g.status) && !hasCanonFailure(g.bgm) && !hasCanonFailure(g.acers)).map((g) => g.group_id)
    );
    const sourceIssueIds = new Set(combinedGroups.filter(isSourceIssueGroup).map((g) => g.group_id));
    const unmatchedIds = new Set(
      combinedGroups
        .filter((g) => (g.status === "UNMATCHED_BGM" || g.status === "UNMATCHED_ACERS") && !isFailureGroup(g))
        .map((g) => g.group_id)
    );
    const failureIds = new Set(combinedGroups.filter(isFailureGroup).map((g) => g.group_id));
    return { matchedIds, mismatchIds, unmatchedIds, failureIds , sourceIssueIds};
  }, [combinedGroups]);

  // ── Tab-filtered rows for BOTH side tables (same group_id filter applied to each side) ──

  const rowsByTab = useMemo(() => {
    const filterGroups = (ids: Set<string> | null) =>
      ids ? combinedGroups.filter((g) => ids.has(g.group_id)) : combinedGroups;

    return {
      all: buildAlignedSides(combinedGroups),
      matched: buildAlignedSides(filterGroups(groupIdsByTab.matchedIds)),
      mismatches: buildAlignedSides(filterGroups(groupIdsByTab.mismatchIds)),
      unmatched: buildAlignedSides(filterGroups(groupIdsByTab.unmatchedIds)),
      failures: buildAlignedSides(combinedGroups.filter(isFailureGroup)),
      sourceIssue: buildAlignedSides(combinedGroups.filter(isSourceIssueGroup)),
    };
  }, [combinedGroups, groupIdsByTab]);

  const activeRows = rowsByTab[activeTab];

  const expandedGroupIdRef = useRef(expandedGroupId);
  useEffect(() => { expandedGroupIdRef.current = expandedGroupId; }, [expandedGroupId]);

  const handleExpandClickRef = useRef(handleExpandClick);
  useEffect(() => { handleExpandClickRef.current = handleExpandClick; }, [handleExpandClick]);

  // ── Side-table columns (shared shape, BGM side additionally shows Canon Status) ──
  const buildSideColumns = (side: "acers" | "bgm"): MRT_ColumnDef<ReconTradeItem>[] => [
    {
      id: "expand",
      header: "",
      size: 34,
      enableSorting: false,
      enableColumnActions: false,
      enableResizing: false,
      enableColumnOrdering: false,
      enablePinning: false,
      muiTableHeadCellProps: { sx: { padding: "0 4px" } },
      muiTableBodyCellProps: { sx: { padding: "0 4px" } },
      Cell: ({ row }) => {
         if ((row.original as AlignedRow).is_placeholder) return null;
        const isOpen = expandedGroupIdRef.current === row.original.group_id;
        return (
          <IconButton size="small" onClick={() => handleExpandClickRef.current(row.original.group_id)} sx={{ p: 0 }}>
            {isOpen
              ? <KeyboardArrowDownIcon sx={{ fontSize: 14, color: "#008c9e" }} />
              : <KeyboardArrowRightIcon sx={{ fontSize: 14, color: "#00bcd4" }} />}
          </IconButton>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 140,
      Cell: ({ cell }) => <StatusChip status={cell.getValue<string>()} />,
    },
    {
      accessorKey: "trade_ids",
      header: "Trade ID",
      size: 170,
      Cell: ({ cell }) => <TradeIdCell ids={cell.getValue<string[]>()} />,
    },
    { accessorKey: "trade_date", header: "Trade Date", size: 90, Cell: ({ cell }) => cell.getValue<string>() ?? "—" },
    { accessorKey: "client", header: "Client", size: 150, Cell: ({ cell }) => cell.getValue<string>() ?? "—" },
    { accessorKey: "contract", header: "Contract", size: 80, Cell: ({ cell }) => cell.getValue<string>() ?? "—" },
    { accessorKey: "contract_month", header: "Contract Month", size: 100, Cell: ({ cell }) => cell.getValue<string>() ?? "—" },
    { accessorKey: "direction", header: "Dir", size: 50, Cell: ({ cell }) => directionCell(cell.getValue<string>()) },
    { accessorKey: "quantity", header: "Canonical Qty", size: 100, Cell: ({ cell }) => qtyPriceCell(cell.getValue<number | null>()) },
    { accessorKey: "price", header: "Price", size: 90, Cell: ({ cell }) => qtyPriceCell(cell.getValue<number | null>()) },
    {
      id: "reasons",
      header: "Break Reason",
      size: 190,
      Cell: ({ row }) => <BreakReasonCell reasons={row.original.reasons} />,
    },
    {
      accessorKey: "canonicalisation_status",
      header: "Canonical Status",
      size: 150,
      Cell: ({ cell }) => {
        const value = cell.getValue<string[] | undefined>();
        if (!value || value.length === 0) {
          return <Typography sx={{ fontSize: "0.72rem", color: "#ccc" }}>—</Typography>;
        }
        return (
          <Box sx={{ display: "flex", gap: 0.4, flexWrap: "nowrap", overflow: "hidden" }}>
            {value.map((status, idx) => (
              <Chip key={idx} label={status} size="small" color={status === "SUCCESS" ? "success" : "error"} sx={{ fontSize: "0.6rem", height: 18 }} />
            ))}
          </Box>
        );
      },
    },
  ];

  const acersColumns = useMemo(() => buildSideColumns("acers"), [expandedGroupId, handleExpandClick]);
  const bgmColumns = useMemo(() => buildSideColumns("bgm"), [expandedGroupId, handleExpandClick]);

  const rowHighlightSx = useCallback(
    (row: AlignedRow) => ({
      height: ROW_HEIGHT,
      backgroundColor: expandedGroupId === row.group_id ? "#e0f7fa" : "transparent",
      opacity: row.is_placeholder ? 0.35 : 1,
    }),
    [expandedGroupId]
  );
  const acersTable = useSideTable<ReconTradeItem>({
    columns: acersColumns,
    data: activeRows.acers,
    getRowId: (r) => `${r.group_id}-acers`,
    scrollRef: mainScroll.leftRef,
    onScroll: () => mainScroll.sync("left"),
    rowSx: rowHighlightSx,
    csvExport: () => exportReconCSV(activeTab, "acers", activeRows.acers, runId),
  });

  const bgmTable = useSideTable<ReconTradeItem>({
    columns: bgmColumns,
    data: activeRows.bgm,
    getRowId: (r) => `${r.group_id}-bgm`,
    scrollRef: mainScroll.rightRef,
    onScroll: () => mainScroll.sync("right"),
    rowSx: rowHighlightSx,
    csvExport: () => exportReconCSV(activeTab, "bgm", activeRows.bgm, runId),
  });

  const tabLabel = useCallback(
    (label: string, count: number) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {label}
        {hasRun && (
          <Box sx={{
            fontSize: "0.62rem", px: 0.7, py: 0.1, borderRadius: "10px",
            backgroundColor: "#00bcd420", color: "#007b8c", fontWeight: 700, lineHeight: 1.6,
          }}>
            {count}
          </Box>
        )}
      </Box>
    ),
    [hasRun]
  );

  // ── Aggregation breakdown — 2 side-by-side MRT tables (same pattern as the old Group Detail) ──
  const breakdownColumns =  (side: "acers" | "bgm"): MRT_ColumnDef<GroupBreakdownTrade>[] => [
    ...(side === "bgm" ? [
      {
        id: "row_number",
        header: "Source Row",
        size: 70,
        Cell: ({ row }) => {
          const rowNum = row.original.break_reason?.[0]?.row;
          return (
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: rowNum == null ? "#ccc" : "#c62828" }}>
              {rowNum ?? "—"}
            </Typography>
          );
        },
      } as MRT_ColumnDef<GroupBreakdownTrade>] : []),
    ...(side === "acers" ? [
      { accessorKey: "source_trade_id", header: "Trade ID", size: 150, Cell: ({ cell }) => cell.getValue<string | null>() ?? "—" } as MRT_ColumnDef<GroupBreakdownTrade>] : []),
    { accessorKey: "source_trade_date", header: "Trade Date", size: 90, Cell: ({ cell }) => cell.getValue<string | null>() ?? "—" },
    { accessorKey: "source_system", header: "System", size: 80 },
    { accessorKey: "source_client_value", header: "Client", size: 130, Cell: ({ cell }) => cell.getValue<string | null>() ?? "—" },
    { accessorKey: "source_contract_symbol", header: "Contract", size: 90, Cell: ({ cell }) => cell.getValue<string | null>() ?? "—" },
    { accessorKey: "source_contract_month", header: "Contract Month", size: 100, Cell: ({ cell }) => cell.getValue<string | null>() ?? "—" },
    { accessorKey: "source_direction_value", header: "Dir", size: 50, Cell: ({ cell }) => directionCell(cell.getValue<string>()) },
    { accessorKey: "source_quantity_value", header: "Lots", size: 70, Cell: ({ cell }) => cell.getValue<string | null>() ?? "—" },
    { accessorKey: "tick", header: "Tick", size: 60, Cell: ({ cell }) => qtyPriceCell(cell.getValue<number | null>()) },
    { accessorKey: "quantity", header: "Canonical Qty (Lots x Tick)", size: 110, Cell: ({ cell }) => qtyPriceCell(cell.getValue<number | null>()) },
    { accessorKey: "source_price_value", header: "Price", size: 80, Cell: ({ cell }) => qtyPriceCell(cell.getValue<number | null>()) },
    {
      id: "break_reason",
      header: "Break Reason",
      size: 190,
      Cell: ({ row }) => <BreakdownReasonCell breakReasons={row.original.break_reason} />,
    },
    {
      accessorKey: "canonicalisation_status",
      header: "Canonical Status",
      size: 110,
      Cell: ({ cell }) => <StatusChip status={cell.getValue<string>()} />,
    },
  ];

  const acersBreakdownColumns = useMemo(() => breakdownColumns("acers"), []);
  const bgmBreakdownColumns = useMemo(() => breakdownColumns("bgm"), []);

  const acersBreakdownTable = useSideTable<GroupBreakdownTrade>({
    columns: acersBreakdownColumns,
    data: breakdown?.acers_trades ?? [],
    getRowId: (r, i) => r.source_trade_id ?? `acers-${i}`,
    scrollRef: detailScroll.leftRef,
    onScroll: () => detailScroll.sync("left"),
    breakdown: true,
    firstColumnId: "source_trade_id",
  });

  const bgmBreakdownTable = useSideTable<GroupBreakdownTrade>({
    columns: bgmBreakdownColumns,
    data: breakdown?.bgm_trades ?? [],
    getRowId: (r, i) => r.source_trade_id ?? `bgm-${i}`,
    scrollRef: detailScroll.rightRef,
    onScroll: () => detailScroll.sync("right"),
    breakdown: true,
    firstColumnId: "row_number",
  });

  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div className="table-ad">
            {/* ── Filters & Actions ── */}
            <Accordion defaultActiveKey="0" className="table-ad__addInputs">
              <Accordion.Item eventKey="0">
                <Accordion.Header className="accordion_button">
                  Reconciliation — Filters & Actions
                </Accordion.Header>
                <Accordion.Body>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: "6px 12px",
                      mb: 1,
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.62rem", color: "#777", fontWeight: 600 }}>
                      View by:
                    </Typography>
                    <RadioGroup
                      row
                      value={viewType}
                      onChange={handleViewTypeChange}
                      sx={{
                        display: "flex",
                        gap: 0,
                        "& .MuiRadio-root": { p: 0.4 },
                        "& .MuiFormControlLabel-root": { m: 0, mr: 1.5 },
                      }}
                    >
                      <FormControlLabel
                        value="version-1"
                        control={
                          <Radio size="small" sx={{ "&.Mui-checked": { color: "#1976d2" } }} />
                        }
                        label={<Box sx={{ fontSize: "0.72rem", fontWeight: 500 }}>Individual</Box>}
                      />
                      <FormControlLabel
                        value="version-2"
                        control={
                          <Radio size="small" sx={{ "&.Mui-checked": { color: "#1976d2" } }} />
                        }
                        label={<Box sx={{ fontSize: "0.72rem", fontWeight: 500 }}>Aggregated</Box>}
                      />
                    </RadioGroup>
                  </Box>
                  <Box sx={{p: 1.5, backgroundColor: "#fff", borderRadius: "4px", border: "1px solid #e0e0e0" }}>
                    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1 }}>
                      <CompactSelect
                        label="Source" value={filters.source} onChange={(v) => updateFilter("source", v)}
                        options={sourceOptions} name="source" slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX}
                      />
                      {(["start_date", "end_date"] as const).map((key) => (
                        <CompactTextField
                          key={key}
                          label={key === "start_date" ? "Trade Start Date" : "Trade End Date"}
                          value={filters[key]} onChange={(v) => updateFilter(key, v)} name={key} type="date"
                          slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX}
                        />
                      ))}
                      <CompactSelect
                        label="Contract" value={filters.contract} onChange={(v) => updateFilter("contract", v)}
                        options={contractOptions} name="contract" slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX}
                      />
                      <CompactSelect
                        label="Buy / Sell" value={filters.buy_sell} onChange={(v) => updateFilter("buy_sell", v)}
                        options={BUY_SELL_OPTIONS} name="buy_sell" slotProps={{ inputLabel: { shrink: true } }} sx={INPUT_SX}
                      />

                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                        <Typography sx={{ fontSize: "0.62rem", color: "#777" }}>
                          BGM File{lastFileName && ` — ${lastFileName}`}
                        </Typography>
                        <Button
                          variant="outlined" size="small"
                          startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                          onClick={() => fileInputRef.current?.click()}
                          sx={{
                            height: 32, textTransform: "none", fontSize: "0.72rem", minWidth: 120,
                            borderColor: selectedFile ? "#00bcd4" : "#C0C0C0", color: selectedFile ? "#00bcd4" : "inherit",
                          }}
                        >
                          {selectedFile ? "Change File" : "Choose File"}
                        </Button>
                        <input
                          ref={fileInputRef} type="file"
                          accept=".csv,.xlsx,.xlsm,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
                          style={{ display: "none" }} onChange={handleFileChange}
                        />
                      </Box>

                      <Button
                        onClick={handleDownloadTemplate} variant="outlined" size="small"
                        startIcon={<GetAppIcon sx={{ fontSize: 14 }} />}
                        sx={{ height: 32, textTransform: "none", fontSize: "0.72rem", minWidth: 120, color: "#777", borderColor: "#C0C0C0" }}
                      >
                        Download Template
                      </Button>

                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

                      <Button
                        onClick={handleRun} variant="contained" disabled={isRunning || !selectedFile}
                        startIcon={!isRunning && (hasRun ? <ReplayIcon sx={{ fontSize: 15 }} /> : <PlayArrowIcon sx={{ fontSize: 15 }} />)}
                        sx={{
                          height: 32, backgroundColor: "#00bcd4", textTransform: "none", fontSize: "0.75rem", minWidth: 150,
                          "&:hover": { backgroundColor: "#008c9e" }, "&.Mui-disabled": { backgroundColor: "#e0e0e0" },
                        }}
                      >
                        {isRunning ? "Running…" : hasRun ? "Re-run" : "Run Reconciliation"}
                      </Button>

                      <Button
                        onClick={handleClear} variant="outlined" color="inherit"
                        sx={{ height: 32, textTransform: "none", fontSize: "0.75rem", minWidth: 80, color: "#777", borderColor: "#C0C0C0" }}
                      >
                        Clear
                      </Button>
                    </Box>

                    {hasRun && !selectedFile && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#c62828", mt: 0 }}>
                        ⚠ Please re-select your BGM file before re-running.
                      </Typography>
                    )}
                  </Box>
                    {/* ── Summary ── */}
                  {summary && (
                    <Box sx={{ mt: 0, p: 1.5, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, mb: 1 }}>
                        Summary
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {SUMMARY_CARDS.map(({ key, label, accent }) => (
                          <SummaryCard key={key} label={label} value={summary[key] as number} accent={accent} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

          

            {/* ── Results — two side-by-side tables (ACERS / BGM), filtered by tab ── */}
            {hasRun && (
              <Box sx={{
                mt: 1.5, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "4px", height: "calc(100vh - 260px)", // tune this to whatever fits under your filters/summary
                overflowY: "auto",
                overflowX: "hidden",
                position: "relative",
              }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => {
                    setActiveTab(v);
                    setExpandedGroupId(null);
                    setBreakdown(null);
                  }}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    backgroundColor: "#fff",
                    borderBottom: "1px solid #e0e0e0",
                    minHeight: 36,
                    "& .MuiTab-root": { minHeight: 36, fontSize: "0.72rem", textTransform: "none", py: 0 },
                  }}
                >
                  <Tab value={ActiveTabEnum.ALL} label={tabLabel("All", combinedGroups.length)} />
                  <Tab value={ActiveTabEnum.MATCHED} label={tabLabel("Matched", groupIdsByTab.matchedIds.size)} />
                  <Tab value={ActiveTabEnum.MISMATCHES} label={tabLabel("Mismatches", groupIdsByTab.mismatchIds.size)} />
                  <Tab value={ActiveTabEnum.UNMATCHED} label={tabLabel("Unmatched", groupIdsByTab.unmatchedIds.size)} />
                  <Tab value={ActiveTabEnum.FAILURES} label={tabLabel("Failures", groupIdsByTab.failureIds.size)} />
                  <Tab value={ActiveTabEnum.SOURCE_ISSUE} label={tabLabel("Source Issue", groupIdsByTab.sourceIssueIds.size)} />
                </Tabs>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", p: "8px", alignItems: "stretch" }}>
                  <SidePanel side="acers" label="ACERS Trades" count={activeRows.acers.length} table={acersTable} />
                  <SidePanel side="bgm" label="BGM Trades" count={activeRows.bgm.length} table={bgmTable} />
                </Box>

                {/* ── Aggregation Breakdown — expand-on-click for a Trade ID / Group ID ── */}
                {expandedGroupId && (
                  <Box ref={breakdownRef} sx={{ borderTop: "2px solid #00bcd4", p: "10px", backgroundColor: "#fafcfd" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 0.5 }}>
                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#007b8c" }}>
                        Aggregation Breakdown
                      </Typography>
                      <IconButton size="small" onClick={() => { setExpandedGroupId(null); setBreakdown(null); }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>

                    {breakdownLoading && (
                      <Typography sx={{ fontSize: "0.72rem", color: "#777", px: 1, py: 2 }}>
                        Loading breakdown…
                      </Typography>
                    )}

                    {!breakdownLoading && breakdown && (() => {
                      // The breakdown endpoint doesn't return customer/contract/direction
                      // or a difference at the top level — derive them from the trade rows.
                      const sample = breakdown.acers_trades[0] ?? breakdown.bgm_trades[0];
                      const difference =
                        breakdown.acers_quantity_sum != null && breakdown.bgm_quantity_sum != null
                          ? breakdown.acers_quantity_sum - breakdown.bgm_quantity_sum
                          : null;
                      const priceDifference =
                        breakdown.acers_avg_price != null && breakdown.bgm_avg_price != null
                          ? Math.round((breakdown.acers_avg_price - breakdown.bgm_avg_price) * 100) / 100
                          : null;

                      return (
                        <>
                          <Box sx={{
                            display: "flex", flexWrap: "wrap", alignItems: "stretch",
                            backgroundColor: "#fff", border: "1px solid #e3e8ec", borderRadius: "8px",
                            mb: 1, overflow: "hidden", boxShadow: "0 1px 3px rgba(16,24,40,0.05)",
                          }}>
                            <SummaryField label="Customer" value={sample?.client ?? "—"} />
                            <SummaryField label="Contract" value={sample?.contract ?? "—"} />
                            <SummaryField label="Direction" value={sample ? directionCell(sample.direction) : "—"} />
                            <SummaryField label="Month" value={sample?.contract_month ?? "—"} />
                            <SummaryField label="ACERS Qty" value={breakdown.acers_quantity_sum ?? "—"} accent="#1565c0" />
                            <SummaryField label="BGM Qty" value={breakdown.bgm_quantity_sum ?? "—"} accent="#2e7d32" />
                            <SummaryField
                              label="Quantity Difference"
                              value={difference != null ? difference.toFixed(2) : "—"}
                              accent={difference != null && difference !== 0 ? "#c62828" : "#2e7d32"}
                            />
                            {/* <SummaryField label="ACERS Avg Price" value={breakdown.acers_avg_price ?? "—"} accent="#1565c0" />
                            <SummaryField label="BGM Avg Price" value={breakdown.bgm_avg_price ?? "—"} accent="#2e7d32" />
                            <SummaryField
                              label="Price Difference"
                              value={priceDifference != null ? priceDifference.toFixed(2) : "—"}
                              accent={priceDifference != null && priceDifference !== 0 ? "#c62828" : "#2e7d32"}
                            /> */}
                            <Box sx={{ display: "flex", alignItems: "center", px: 1.2, py: 0.5, ml: "auto" }}>
                              <StatusChip status={breakdown.status} />
                            </Box>
                          </Box>

                          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", alignItems: "stretch" }}>
                            <SidePanel
                              side="acers"
                              label="ACERS Trades"
                              count={breakdown.acers_trades.length}
                              table={acersBreakdownTable}
                              footer={<DetailTotalsRow label="ACERS Qty" total={breakdown.acers_quantity_sum} accent="#1565c0" bg="#eef7fb" />}
                            />
                            <SidePanel
                              side="bgm"
                              label="BGM Trades"
                              count={breakdown.bgm_trades.length}
                              table={bgmBreakdownTable}
                              footer={<DetailTotalsRow label="BGM Qty" total={breakdown.bgm_quantity_sum} accent="#2e7d32" bg="#eff8ef" />}
                            />
                          </Box>
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            )}
          </div>
        </Row>
      </Container>

      {formatErrors && <DateFormatErrorModal errors={formatErrors} onClose={() => setFormatErrors(null)} />}
    </div>
  );
};

export default ReconciliationAutomationPage;
